import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Factory,
  Warehouse,
  ShoppingCart,
  Package,
  FileText,
  Boxes,
  GanttChart,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Percent,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useInventory } from '@/context/InventoryContext';
import { AnimatedMetricCard } from '@/components/dashboard/AnimatedMetricCard';

export function DashboardPage() {
  const { monthlyPlan, purchaseOrders, ganttOrders, workOrders } = useInventory();

  // Work Order Stats (from actual work orders)
  const workOrderStats = useMemo(() => {
    const total = workOrders.length;
    const completed = workOrders.filter(wo => wo.status === 'completed').length;
    const inProgress = workOrders.filter(wo => wo.status === 'in_progress').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { total, completed, inProgress, completionRate };
  }, [workOrders]);

  // Inventory & Planning Stats (from monthly plan status)
  const inventoryStats = useMemo(() => {
    // Total systems required (sensor box count = system count)
    const totalSystemsRequired = monthlyPlan.reduce((sum, m) => sum + m.sensorBoxQty, 0);

    // Already supplied (months with status 'completed')
    const alreadySupplied = monthlyPlan.reduce(
      (sum, m) => m.status === 'completed' ? sum + m.sensorBoxQty : sum,
      0
    );

    // Qty to supply (pending)
    const qtyToSupply = totalSystemsRequired - alreadySupplied;

    // Completion rate
    const completionRate = totalSystemsRequired > 0
      ? Math.round((alreadySupplied / totalSystemsRequired) * 100)
      : 0;

    return { totalSystemsRequired, alreadySupplied, qtyToSupply, completionRate };
  }, [monthlyPlan]);

  // Procurement Stats
  const procurementStats = useMemo(() => {
    const totalPOs = purchaseOrders.length;
    const inProgress = purchaseOrders.filter(po => po.status === 'in_progress').length;
    const received = purchaseOrders.filter(po => po.status === 'submitted').length;
    const totalValue = purchaseOrders.reduce((sum, po) => sum + po.total_amount, 0);
    const receivedValue = purchaseOrders
      .filter(po => po.status === 'submitted')
      .reduce((sum, po) => sum + po.total_amount, 0);

    return { totalPOs, inProgress, received, totalValue, receivedValue };
  }, [purchaseOrders]);

  // Total Revenue from Work Orders
  const totalWORevenue = useMemo(() => {
    return workOrders.reduce((sum, wo) => sum + wo.total, 0);
  }, [workOrders]);

  // Project Profit Stats (using Work Orders for revenue, Purchase Orders for costs)
  const profitStats = useMemo(() => {
    const revenue = totalWORevenue;
    const costs = procurementStats.totalValue;

    const profit = revenue - costs;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

    return { revenue, costs, profit, margin };
  }, [totalWORevenue, procurementStats.totalValue]);

  // Gantt View Stats
  const ganttStats = useMemo(() => {
    const totalOrders = ganttOrders.length;
    const allTasks = ganttOrders.flatMap(o => o.tasks);
    const totalTasks = allTasks.length;

    const completedTasks = allTasks.filter(t => t.progress === 100).length;
    const inProgressTasks = allTasks.filter(t => t.progress > 0 && t.progress < 100).length;
    const pendingTasks = allTasks.filter(t => t.progress === 0).length;

    const overallProgress = totalTasks > 0
      ? Math.round(allTasks.reduce((sum, t) => sum + t.progress, 0) / totalTasks)
      : 0;

    return { totalOrders, totalTasks, completedTasks, inProgressTasks, pendingTasks, overallProgress };
  }, [ganttOrders]);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          Overview of your production management system
        </p>
      </div>

      {/* Main Metrics Grid - Fully Responsive */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {/* Work Orders */}
        <AnimatedMetricCard
          title="Work Orders"
          subtitle="Production status"
          icon={Factory}
          mainValue={workOrderStats.total}
          subLabel="Total work orders"
          progressPercentage={workOrderStats.completionRate}
          colorTheme="blue"

          secondaryMetrics={[
            { label: 'Completed', value: workOrderStats.completed, color: 'text-emerald-500' },
            { label: 'In Progress', value: workOrderStats.inProgress, color: 'text-amber-500' },
          ]}
        />

        {/* Inventory */}
        <AnimatedMetricCard
          title="Inventory"
          subtitle="Monthly plan status"
          icon={Warehouse}
          mainValue={inventoryStats.totalSystemsRequired}
          subLabel="Total systems required"
          progressPercentage={inventoryStats.completionRate}
          colorTheme="green"

          secondaryMetrics={[
            { label: 'Supplied', value: inventoryStats.alreadySupplied, color: 'text-emerald-500' },
            { label: 'Pending', value: inventoryStats.qtyToSupply, color: inventoryStats.qtyToSupply > 0 ? 'text-amber-500' : 'text-emerald-500' },
          ]}
        />

        {/* Procurement */}
        <AnimatedMetricCard
          title="Procurement"
          subtitle="Purchase orders"
          icon={ShoppingCart}
          mainValue={procurementStats.totalValue}
          subLabel="Total procurement value"
          progressPercentage={procurementStats.totalPOs > 0 ? (procurementStats.received / procurementStats.totalPOs) * 100 : 0}
          colorTheme="purple"
          
          format="currency"
          secondaryMetrics={[
            { label: 'Total POs', value: procurementStats.totalPOs },
            { label: 'Received', value: procurementStats.received, color: 'text-emerald-500' },
          ]}
        />

        {/* Gantt Progress */}
        <AnimatedMetricCard
          title="Gantt Progress"
          subtitle="Project timeline"
          icon={GanttChart}
          mainValue={ganttStats.overallProgress}
          subLabel="Overall task completion"
          progressPercentage={ganttStats.overallProgress}
          colorTheme="cyan"
          
          format="percentage"
          secondaryMetrics={[
            { label: 'Tasks Done', value: ganttStats.completedTasks, color: 'text-emerald-500' },
            { label: 'In Progress', value: ganttStats.inProgressTasks, color: 'text-amber-500' },
          ]}
        />
      </div>

      {/* Financial Overview Section */}
      <div>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold">Financial Overview</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Real-time profit metrics</p>
          </div>
          <Link to="/cashflow">
            <Button variant="outline" size="sm" className="gap-1 text-xs sm:text-sm">
              View Details
            </Button>
          </Link>
        </div>

        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Revenue */}
          <AnimatedMetricCard
            title="Total Revenue"
            subtitle="From work orders"
            icon={DollarSign}
            mainValue={totalWORevenue}
            subLabel={workOrders.length > 0 ? `${workOrders.length} Work Orders` : 'No orders yet'}
            progressPercentage={totalWORevenue > 0 ? 100 : 0}
            colorTheme="green"
            
            format="currency"
            secondaryMetrics={[
              { label: 'Orders', value: workOrders.length },
              { label: 'Status', value: totalWORevenue > 0 ? 'Active' : 'Pending', color: totalWORevenue > 0 ? 'text-emerald-500' : 'text-slate-400' },
            ]}
          />

          {/* Total Cost */}
          <AnimatedMetricCard
            title="Total Cost"
            subtitle="From purchase orders"
            icon={TrendingDown}
            mainValue={procurementStats.totalValue}
            subLabel={procurementStats.totalPOs > 0 ? 'From Purchase Orders' : 'No costs yet'}
            progressPercentage={procurementStats.totalPOs > 0 ? (procurementStats.received / procurementStats.totalPOs) * 100 : 0}
            colorTheme="orange"
            
            format="currency"
            secondaryMetrics={[
              { label: 'POs', value: procurementStats.totalPOs },
              { label: 'In Progress', value: procurementStats.inProgress, color: 'text-amber-500' },
            ]}
          />

          {/* Net Profit */}
          <AnimatedMetricCard
            title="Net Profit"
            subtitle="Revenue - Costs"
            icon={TrendingUp}
            mainValue={Math.abs(profitStats.profit)}
            subLabel={profitStats.profit >= 0 ? 'Profitable' : 'At Loss'}
            progressPercentage={profitStats.margin > 0 ? Math.min(profitStats.margin, 100) : 0}
            colorTheme={profitStats.profit >= 0 ? 'cyan' : 'orange'}
            
            format="currency"
            secondaryMetrics={[
              { label: 'Status', value: profitStats.profit >= 0 ? 'Profit' : 'Loss', color: profitStats.profit >= 0 ? 'text-emerald-500' : 'text-rose-500' },
              { label: 'Trend', value: profitStats.profit >= 0 ? '+' : '-', color: profitStats.profit >= 0 ? 'text-emerald-500' : 'text-rose-500' },
            ]}
          />

          {/* Profit Margin */}
          <AnimatedMetricCard
            title="Profit Margin"
            subtitle="Percentage"
            icon={Percent}
            mainValue={Math.abs(profitStats.margin)}
            subLabel={profitStats.margin >= 15 ? 'Healthy' : profitStats.margin >= 0 ? 'Moderate' : 'At Loss'}
            progressPercentage={Math.max(0, Math.min(profitStats.margin, 100))}
            colorTheme={profitStats.margin >= 15 ? 'blue' : profitStats.margin >= 0 ? 'purple' : 'orange'}
            
            format="percentage"
            secondaryMetrics={[
              { label: 'Health', value: profitStats.margin >= 15 ? 'Good' : profitStats.margin >= 0 ? 'OK' : 'Poor', color: profitStats.margin >= 15 ? 'text-emerald-500' : profitStats.margin >= 0 ? 'text-amber-500' : 'text-rose-500' },
              { label: 'Target', value: '15%', color: 'text-slate-400' },
            ]}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <Link to="/work-order">
              <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1.5 sm:gap-2">
                <Factory className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                <span className="text-xs sm:text-sm">New Work Order</span>
              </Button>
            </Link>
            <Link to="/procurement">
              <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1.5 sm:gap-2">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                <span className="text-xs sm:text-sm">Create PO</span>
              </Button>
            </Link>
            <Link to="/inventory">
              <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1.5 sm:gap-2">
                <Boxes className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-500" />
                <span className="text-xs sm:text-sm">View Inventory</span>
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1.5 sm:gap-2">
                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
                <span className="text-xs sm:text-sm">Products</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
